import { BP_VISITOR_ID_PARAM } from "../visitor/constants";
import {
  GCLID_PARAM,
  GBRAID_PARAM,
  WBRAID_PARAM,
} from "../clickIds/constants";
import type { ClickIds } from "../clickIds/types";
import { UTM_PARAMS } from "../utm/constants";
import type { UtmParams } from "../utm/types";

const WEB_APP_HOST = "app.briefpanel.com";
const MAX_HANDOFF_VALUE_LENGTH = 200;

type VisitorHandoffParams = {
  visitorId?: string | null;
  clickIds?: ClickIds | null;
  utm?: UtmParams | null;
};

type HandoffOptions = {
  /**
   * Overrides the destination origin for the canonical web app host. Consumers
   * pass their environment-specific URL (e.g. a localhost dev server) so the
   * shared package never has to read framework-specific env vars itself.
   */
  webAppUrl?: string | null;
};

function normalizeHandoffValue(value: string | null | undefined) {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.length > MAX_HANDOFF_VALUE_LENGTH) {
    return null;
  }
  return trimmed;
}

function resolveWebAppHref(url: URL, webAppUrl: string | null | undefined) {
  if (!webAppUrl || url.hostname !== WEB_APP_HOST) {
    return url;
  }

  let base: URL;
  try {
    base = new URL(webAppUrl);
  } catch {
    return url;
  }

  return new URL(`${url.pathname}${url.search}${url.hash}`, base);
}

export function appendVisitorHandoffParams(
  href: string,
  params: VisitorHandoffParams,
  options: HandoffOptions = {},
) {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }

  if (url.protocol !== "https:" || url.hostname !== WEB_APP_HOST) {
    return href;
  }

  const targetUrl = resolveWebAppHref(url, options.webAppUrl);
  const visitorId = normalizeHandoffValue(params.visitorId);
  if (visitorId) {
    targetUrl.searchParams.set(BP_VISITOR_ID_PARAM, visitorId);
  }

  const clickIds = params.clickIds;
  if (clickIds?.gclid) {
    targetUrl.searchParams.set(GCLID_PARAM, clickIds.gclid);
  }
  if (clickIds?.gbraid) {
    targetUrl.searchParams.set(GBRAID_PARAM, clickIds.gbraid);
  }
  if (clickIds?.wbraid) {
    targetUrl.searchParams.set(WBRAID_PARAM, clickIds.wbraid);
  }

  const utm = params.utm;
  if (utm) {
    for (const key of UTM_PARAMS) {
      const value = normalizeHandoffValue(utm[key]);
      if (value) {
        targetUrl.searchParams.set(key, value);
      }
    }
  }

  return targetUrl.toString();
}
